import { useEffect, useRef } from 'react';
import gsap from 'gsap';
// The CJS build, not `gsap/Observer`. That entry is ESM-only, and CRA's Jest
// does not transform node_modules — importing it makes every suite that
// touches SlideContainer fail to parse. This is the first GSAP plugin used in
// the app, so there was no existing convention to copy.
import Observer from 'gsap/dist/Observer';
import {
  initialSwipeGestureState,
  type Neighbours,
  stepSwipeGesture,
  stopSwipeGesture
} from 'hooks/trackpadSwipeMachine';
import { isSwipeIgnoredTarget } from 'utils/swipeIgnore';

import { DRAG_START_PX } from 'components/RebindingTableView/swipeCommit';

// Re-exported so existing imports and tests keep one canonical home; the
// values and their rationale now live with the machine they parameterise.
export {
  gestureDeltaSign,
  NEW_BURST_MIN_DELTA_PX,
  NEW_BURST_MIN_VELOCITY_PX_S,
  SAME_GESTURE_LULL_MS
} from 'hooks/trackpadSwipeMachine';

gsap.registerPlugin(Observer);

/**
 * Trackpad / Magic Mouse horizontal swipe → table switch.
 *
 * The touch gesture in SlideContainer was the only way to change tables by
 * gesture, so desktop had none at all: a two-finger trackpad swipe emits
 * `wheel` events with `deltaX`, never touch events. This adds the missing
 * input and feeds it through the SAME begin/update/end drag channel the touch
 * path uses, so both inputs share one commit rule, one animation and one set
 * of edge behaviours — they cannot drift apart.
 *
 * Reusing that channel is also what keeps the fixed-position chrome safe: the
 * track only ever carries a transform while a drag is live and clears it at
 * rest (see RebindingTableView — a transform on the track re-bases every
 * `position: fixed` descendant). A separate wheel implementation with its own
 * transform would have quietly broken every modal and rail in the table view.
 *
 * ## Where the logic lives
 *
 * Everything that DECIDES — one commit per physical gesture, the inertia-tail
 * swallowing, the three-signal re-arm — is the pure machine in
 * `trackpadSwipeMachine.ts`, unit-tested event by event. This hook owns only
 * the environment: the Observer, pointer blocking, click swallowing, and
 * teardown.
 *
 * ## Why GSAP Observer rather than a raw `wheel` listener or a library
 *
 * GSAP is already a dependency (122 files) and since 3.13 Observer ships in
 * the package — +4.3 kB gzip, no new dependency, no supply chain. It
 * normalises wheel deltas across browsers, owns the non-passive listener
 * (React's `onWheel` is ALWAYS registered passive, so `preventDefault` is
 * impossible through JSX), and that `preventDefault` is also the only thing
 * that stops macOS Safari's two-finger history swipe-back from firing under
 * our gesture — `overscroll-behavior-x` does not (WebKit bug 240183, still
 * open).
 *
 * ## The momentum problem
 *
 * `wheel` has no gesture-end event: trackpad inertia keeps firing a decaying
 * tail of deltas long after the fingers lift. Observer's `onStop` fires once
 * changes cease for `onStopDelay`, which is the practical end-of-gesture
 * signal. The machine's `committed` latch is what stops a single flick —
 * whose inertia tail easily crosses the threshold several times — from paging
 * through three tables: once a gesture has decided, every further delta from
 * that same gesture is ignored until `onStop` (or a genuine new burst) resets
 * it.
 *
 * iPhones never emit `wheel`, but `type: 'wheel,pointer'` ALSO subscribes
 * non-passive `pointer*` listeners with `preventDefault: true` — and on a
 * touch device those listeners still fire (touch input dispatches pointer
 * events). Even with the `pointerBlocked` guard in `onChangeX`, the
 * `preventDefault: true` at the Observer level runs BEFORE the guard, which
 * blocks the browser's native vertical scroll on every panel descending from
 * the target — reproduced as "chat and table history stop scrolling once you
 * observe a second table" (the enabled gate flips true then). Callers must
 * therefore gate `enabled` on a hover-pointer signal (see
 * SlideContainer.tsx's `useSupportsHover()` usage) so the observer never
 * mounts on touch-only devices; the touch swipe path lives elsewhere and
 * already covers them.
 *
 * That gate fixes TOUCH only. On DESKTOP the same preventDefault killed the
 * same two panels, and the gate cannot help because a mouse is the very input
 * this path exists for — the Observer is supposed to be armed. Those regions
 * are excluded individually instead, through `ignoreCheck` (below) and the
 * `data-swipe-ignore` tag they wear; see utils/swipeIgnore. GSAP consults
 * `ignoreCheck` before it ever calls preventDefault, which is what makes the
 * two compatible: the felt keeps its swipe-back suppression, the panels keep
 * their scroll.
 */

type Options = {
  /** Element the gesture is read from — the table surface. */
  target: React.RefObject<HTMLElement>;
  /** Whether a table swipe is allowed right now (view, table count, motion pref). */
  enabled: boolean;
  /** Open tables either side of the one on screen, or null when unavailable. */
  getNeighbours: () => Neighbours | null;
  /**
   * Gesture accepted: returns the offset this gesture's travel adds to (0 from
   * rest, or wherever a grabbed spring-back had got to). Returns null to refuse
   * the gesture entirely — the caller's decision, since only it knows what else
   * is happening on the surface.
   */
  onDragStart: (session: Neighbours) => number | null;
  onDragMove: (offsetPx: number) => void;
  /** Release: `target` is the gameID to settle to, or null to spring back. */
  onDragEnd: (target: string | null, velocityPxPerMs: number) => void;
};

export function useTrackpadTableSwipe({
  target,
  enabled,
  getNeighbours,
  onDragStart,
  onDragMove,
  onDragEnd
}: Options): void {
  // THE OBSERVER MUST SURVIVE RE-RENDERS. These callbacks get fresh identities
  // whenever the header re-renders (getNeighbours closes over activeTables,
  // which is rebuilt per render) — and with them in the effect's deps, every
  // re-render tore the Observer down and attached a new one. A gesture that
  // itself CAUSES a re-render (the first one after a table opens: beginSwipeDrag
  // → view state → render) killed its own listener mid-stream. The remaining
  // wheel deltas then hit a page with no non-passive handler, so nothing
  // committed and — in Chrome — the unconsumed swipe armed history navigation:
  // the "first swipe flashes brown, second works" bug (Jason 2026-08-17; Safari
  // was immune because its history gesture starts from the screen edge only).
  // Latest callbacks live in a ref the handlers read at CALL time, so the
  // effect deps are just [target, enabled] and the listener's lifetime matches
  // the surface's.
  const callbacks = useRef({ getNeighbours, onDragStart, onDragMove, onDragEnd });
  callbacks.current = { getNeighbours, onDragStart, onDragMove, onDragEnd };

  useEffect(() => {
    const el = target.current;
    if (!el || !enabled) return;

    const gesture = initialSwipeGestureState();

    // Mouse click-and-drag paging (EN-10545, JRam's ask: a conventional mouse
    // has no horizontal wheel, so dragging the felt is the natural gesture).
    // Rides the same Observer + the same drag channel as the wheel path.
    // Guards, in order of importance:
    //   • mouse only — touch already has its own handler in SlideContainer,
    //     and letting Observer's pointer events double-handle it would run two
    //     drags per finger.
    //   • never begin on an interactive element: a drag that started on FOLD
    //     and released there would still dispatch its click — on a poker
    //     action. Checked at press time via closest().
    //   • after a real drag, swallow the trailing click for one tick so the
    //     release can't activate whatever ended up under the cursor.
    let pointerBlocked = false;
    let suppressClickUntil = 0;
    /**
     * Every event family Observer can deliver a drag through.
     *
     * Observer picks ONE family at load and sticks to it (Observer.js:230):
     * `touch*` when the document has `ontouchstart`, else `mouse*` when it has
     * no `onpointerdown`, else `pointer*`. All three must be recognised here or
     * the guard below silently stops applying on whichever family is in play.
     */
    const isPointerLike = (type: string): boolean =>
      type.startsWith('pointer') || type.startsWith('touch') || type.startsWith('mouse');
    /**
     * Is this a real mouse, the one input the trackpad path is FOR?
     *
     * Recognised by family as well as by `pointerType`, because a MouseEvent has
     * no `pointerType` at all. Testing `pointerType !== 'mouse'` alone would read
     * every `mousedown` as not-a-mouse and block the drag outright — the mouse
     * fallback would arm the guard against the very gestures it exists to allow.
     */
    const isMouseInput = (ev: PointerEvent): boolean =>
      ev.type.startsWith('mouse') || ev.pointerType === 'mouse';
    const isInteractive = (t: EventTarget | null): boolean =>
      t instanceof Element &&
      !!t.closest('button, a, input, select, textarea, [role="button"], [role="switch"]');
    const swallowClick = (e: MouseEvent) => {
      if (Date.now() < suppressClickUntil) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    el.addEventListener('click', swallowClick, true);

    const observer = Observer.create({
      target: el,
      type: 'wheel,pointer',
      /**
       * Hands a gesture back to the browser when it starts somewhere that owns
       * itself — a panel that scrolls (chat, table history) or a control with
       * its own horizontal drag (the raise slider). Same rule SlideContainer's
       * touch path applies; see utils/swipeIgnore.
       *
       * This has to be `ignoreCheck` and not the `pointerBlocked` guard used in
       * `onChangeX`. GSAP consults `ignoreCheck` FIRST and returns before it
       * ever calls `preventDefault` (Observer's `_onWheel`/`_onMove`/`_onPress`
       * all run `_ignoreCheck(e)` ahead of `_getEvent(e, preventDefault)`),
       * whereas `pointerBlocked` is read inside our own callback — long after
       * the default has already been cancelled. Suppressing the swipe was never
       * the problem; suppressing the SCROLL was.
       *
       * Registration stays non-passive either way (GSAP derives `passive` from
       * `preventDefault` when the Observer is built, not per event), so the
       * Safari swipe-back suppression this option exists for is unaffected on
       * the felt itself.
       */
      ignoreCheck: (e: Event) => isSwipeIgnoredTarget(e?.target),
      onPress: (self) => {
        const ev = self.event as PointerEvent;
        // TOUCH counts too. This tested only `pointer*`, but GSAP Observer
        // reports TOUCH events on iOS — so a finger arrived as 'touchstart',
        // failed the test, and left pointerBlocked false. The gesture then ran
        // the full trackpad path alongside the real touch handlers and
        // committed the swipe a second time (measured +23ms, 2026-09-09).
        //
        // Block anything that is NOT a mouse (a finger has its own handler in
        // SlideContainer), and any mouse press that begins on a control. Note
        // the mouse test is `isMouseInput`, not `pointerType !== 'mouse'`: on
        // the mouse fallback family there is no pointerType to read.
        pointerBlocked = isPointerLike(ev.type) && (!isMouseInput(ev) || isInteractive(ev.target));
      },
      onRelease: () => {
        if (gesture.dragging || gesture.committed) suppressClickUntil = Date.now() + 150;
        pointerBlocked = false;
      },
      // Horizontal only, two ways: `lockAxis` pins a gesture to whichever axis
      // it starts on, and we only handle `onChangeX`, so a vertical scroll is
      // never read as a table switch. A conventional mouse wheel emits no
      // deltaX at all, which makes this naturally trackpad / Magic Mouse /
      // shift-wheel without any device sniffing (there is no reliable API for
      // that anyway).
      lockAxis: true,
      // Non-passive: needed to suppress Safari's history swipe-back.
      preventDefault: true,
      tolerance: DRAG_START_PX,
      onChangeX: (self) => {
        const evType = String(self.event?.type || '');
        // A blocked pointer gesture (touch, or started on a control) is not a
        // table drag. Wheel events are never blocked by this flag.
        if (pointerBlocked && isPointerLike(evType)) return;
        const action = stepSwipeGesture(
          gesture,
          {
            eventType: evType,
            deltaX: self.deltaX,
            velocityXPxPerS: self.velocityX ?? 0,
            nowMs: Date.now()
          },
          callbacks.current.getNeighbours(),
          window.innerWidth || 1,
          callbacks.current.onDragStart
        );
        if (action.kind === 'commit') {
          callbacks.current.onDragEnd(action.targetId, action.velocityPxPerMs);
        } else if (action.kind === 'move') {
          callbacks.current.onDragMove(action.offsetPx);
        }
      },
      onStop: () => {
        // Only reached when the gesture ended WITHOUT deciding — a short nudge,
        // or a pull at an edge that never cleared the bar. Always a spring
        // back; a committing gesture has already returned from onChangeX.
        if (stopSwipeGesture(gesture).springBack) callbacks.current.onDragEnd(null, 0);
      },
      onStopDelay: 0.12
    });

    return () => {
      // ABORT, don't strand. If this teardown lands mid-drag (enabled flipped —
      // e.g. the open-table list churned below 2 for a frame during a
      // re-subscribe), the gesture has already armed the 60-second switch lock
      // via beginSwipeDrag, and nothing else would ever release it: onStop dies
      // with the observer, so every later swipe is refused until a tile click
      // runs a programmatic settle. That is the "misses entirely and won't
      // reset until I select a tile" failure (Jason 2026-08-17). A null
      // onDragEnd is the same calm spring-back a cancelled touch gets.
      if (gesture.dragging && !gesture.committed) callbacks.current.onDragEnd(null, 0);
      el.removeEventListener('click', swallowClick, true);
      observer.kill();
    };
    // Callbacks are read through the ref above — deliberately not deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, enabled]);
}
